import React from 'react';
import {
    ExamQuestionImage,
    CKText,
    CheckedTable,
    EditableTable,
} from 'components';
import {
    ITheoreticalQuestion,
    IPracticalQuestion,
    IFactualTheoreticalQuestionAnswer,
    IFactualPracticalQuestionAnswer,
} from 'components/models';
import { PracticalQuestionType } from 'components/enums';
import { serialNumberToChar, getImageUrlFromText } from '_helpers';
import cx from 'classnames';
import styles from './ExamQuestion.module.scss';

const ExamQuestion = ({
    question, num, theoreticalFactual, practicalFactual, withoutResult,
}: IProps) => {
    const correctAnswer = (answerNumber: number) => theoreticalFactual && theoreticalFactual.correctAnswersNumbers.find(correct => correct.number === answerNumber);
    const userSelect = (answerNumber: number) => theoreticalFactual && theoreticalFactual.factualAnswersNumbers.find(selected => selected.number === answerNumber);
    const correctMatchingAnswer = (answerNumber: number, matchingNumber: number) => theoreticalFactual && theoreticalFactual.correctAnswersNumbers.find(correct => correct.number === answerNumber && correct.matchingNumber === matchingNumber);

    const theoreticalQuestion = question as ITheoreticalQuestion;
    if (!theoreticalQuestion) return null;
    const { imageUrls, textParts } = getImageUrlFromText(question.text);

    return (
        <div className={styles.question}>
            <span className={styles.question_title}>Вопрос {num}</span>
            <span className={styles.question_text}>
                {textParts.map((text, i) => (
                    <div key={`question_text_${i}`}>
                        <CKText text={text} />
                        {imageUrls[i] && <ExamQuestionImage imageUrl={imageUrls[i]} />}
                    </div>
                ))}
            </span>
            {practicalFactual && practicalFactual.criteriaForRate && (
                <>
                    <strong className="d-block">Критерии оценки</strong>
                    <div className="mb-4">
                        <CKText text={practicalFactual.criteriaForRate} />
                    </div>
                </>
            )}
            {(theoreticalQuestion.type === 'ordering' || theoreticalQuestion.type === 'matching') && (
                <div className="mb-3">
                    <span className={styles.question_title}>{theoreticalQuestion.answersHeader}</span>
                    {theoreticalQuestion.answers.map((answer, i) => (
                        <div className="d-block mt-2 mb-2" key={`answer_matching_${i}`}>
                            <span>{serialNumberToChar(answer.number)}.</span>
                            <span className="ml-2">{answer.text}</span>
                        </div>
                    ))}
                </div>
            )}
            {(!practicalFactual || practicalFactual.type !== PracticalQuestionType.Offline) && (
                <span className={styles.question_title}>
                    {theoreticalQuestion.type === 'ordering' || theoreticalQuestion.type === 'matching' ? theoreticalQuestion.matchingAnswersHeader : 'ответ'}
                </span>
            )}
            {practicalFactual && practicalFactual.essayAnswer ? (
                <span className={styles.question_text}>{practicalFactual.essayAnswer.factual}</span>
            ) : null}
            {practicalFactual && practicalFactual.openAnswerAnswer ? (
                <>
                    <span className={cx(styles.question_text, practicalFactual.correct ? styles.question_text_correct : styles.question_text_wrong)}>{practicalFactual.openAnswerAnswer.factual}</span>
                    {!practicalFactual.correct ? (
                        <>
                            <span className={styles.question_title}>Верный ответ:</span>
                            <span className={cx(styles.question_text, styles.question_text_correct)}>{practicalFactual.openAnswerAnswer.correct}</span>
                        </>
                    ) : null}
                </>
            ) : null}
            {practicalFactual && practicalFactual.tabularAnswer ? (
                <div className="mt-4">
                    {practicalFactual.tabularAnswer.map((table, i) => (
                        <div key={`checkedTable_${i}`}>
                            {table.showCorrectAndApplicantAnswer ? (
                                <>
                                    <span className={styles.caption}>{table.caption}</span>
                                    <EditableTable
                                      table={{
                                            ...table,
                                            initialRows: table.applicantAnswerRows,
                                            caption: 'Решение соискателя',
                                        }}
                                      headersSeparator="#"
                                      onlyWatch
                                    />
                                    <EditableTable
                                      table={{
                                            ...table,
                                            initialRows: table.correctAnswerRows,
                                            caption: 'Правильный ответ',
                                        }}
                                      headersSeparator="#"
                                      onlyWatch
                                    />
                                </>
                            ) : (!table.correctAnswerRows || table.correctAnswerRows.length === 0 ? (
                                <EditableTable
                                  table={{
                                        ...table,
                                        initialRows: table.applicantAnswerRows,
                                    }}
                                  headersSeparator="#"
                                  onlyWatch
                                />
                            ) : (
                                    <CheckedTable
                                      table={table}
                                      checkedCell={table.rows || []}
                                      headersSeparator="#"
                                      tableType={practicalFactual.type === PracticalQuestionType.ManualCheckTabular ? 'manual' : 'autoCheck'}
                                    />
                                )
                                )}
                        </div>
                    ))}
                </div>
            ) : null}
            <ul className={styles.list}>
                {theoreticalFactual && (
                    <>
                        {!theoreticalQuestion.matchingAnswers && theoreticalQuestion.answers.length ? theoreticalQuestion.answers.map((answer, j) => (
                            <li className={styles.list_item} key={`question_${j}`}>
                                <div className={cx(styles.checkbox, userSelect(answer.number) && styles.checkbox_checked, correctAnswer(answer.number) && styles.checkbox_correct)}>
                                    {userSelect(answer.number) && (
                                        <svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M15.7143 9.23661V12.7857C15.7143 13.6711 15.3999 14.4282 14.7712 15.0569C14.1425 15.6856 13.3854 16 12.5 16H3.21429C2.32886 16 1.5718 15.6856 0.94308 15.0569C0.314357 14.4282 0 13.6711 0 12.7857V3.50001C0 2.61459 0.314357 1.85752 0.94308 1.2288C1.5718 0.600078 2.32886 0.285721 3.21429 0.285721H12.5C12.9688 0.285721 13.404 0.378726 13.8058 0.564739C13.9174 0.616822 13.9844 0.702387 14.0067 0.821435C14.029 0.947924 13.9955 1.05581 13.9062 1.1451L13.3594 1.69197C13.285 1.76638 13.1994 1.80358 13.1027 1.80358C13.0804 1.80358 13.0469 1.79614 13.0022 1.78126C12.8311 1.73661 12.6637 1.71429 12.5 1.71429H3.21429C2.72321 1.71429 2.30283 1.88914 1.95312 2.23885C1.60342 2.58855 1.42857 3.00893 1.42857 3.50001V12.7857C1.42857 13.2768 1.60342 13.6972 1.95312 14.0469C2.30283 14.3966 2.72321 14.5714 3.21429 14.5714H12.5C12.9911 14.5714 13.4115 14.3966 13.7612 14.0469C14.1109 13.6972 14.2857 13.2768 14.2857 12.7857V9.9509C14.2857 9.85417 14.3192 9.77233 14.3862 9.70536L15.1004 8.99108C15.1749 8.91667 15.2604 8.87947 15.3571 8.87947C15.4018 8.87947 15.4464 8.89063 15.4911 8.91295C15.6399 8.97248 15.7143 9.08036 15.7143 9.23661ZM18.2924 3.77902L9.20759 12.8638C9.02902 13.0424 8.81697 13.1317 8.57143 13.1317C8.32589 13.1317 8.11384 13.0424 7.93527 12.8638L3.13616 8.06474C2.95759 7.88617 2.8683 7.67412 2.8683 7.42858C2.8683 7.18304 2.95759 6.97099 3.13616 6.79242L4.36384 5.56474C4.54241 5.38617 4.75446 5.29688 5 5.29688C5.24554 5.29688 5.45759 5.38617 5.63616 5.56474L8.57143 8.50001L15.7924 1.27902C15.971 1.10045 16.183 1.01117 16.4286 1.01117C16.6741 1.01117 16.8862 1.10045 17.0647 1.27902L18.2924 2.5067C18.471 2.68528 18.5603 2.89733 18.5603 3.14286C18.5603 3.3884 18.471 3.60045 18.2924 3.77902Z" />
                                        </svg>
                                    )}
                                </div>
                                <span>{answer.text}</span>

                                {practicalFactual && (
                                    <span className={practicalFactual.correct ? styles.success_text : styles.failed_text}>{practicalFactual.openAnswerAnswer && practicalFactual.openAnswerAnswer.factual}</span>
                                )}
                            </li>
                        )) : theoreticalQuestion.matchingAnswers && theoreticalFactual.factualAnswersNumbers.sort((a, b) => a.number - b.number).map((answer, j) => (
                            <li className={styles.list_item} key={`matching_question_${j}`}>
                                <span>{answer.number}.</span>
                                <span className={cx(styles.matching, correctMatchingAnswer(answer.number, answer.matchingNumber || 1) && styles.matching_correct)}>{serialNumberToChar(answer.matchingNumber || 1)}</span>
                                {theoreticalQuestion.type !== 'ordering' && theoreticalQuestion.matchingAnswers && theoreticalQuestion.matchingAnswers[answer.number - 1] && (
                                    <span className="ml-2">{theoreticalQuestion.matchingAnswers[answer.number - 1].text}</span>
                                )}
                            </li>
                        ))}

                        {theoreticalFactual.factualAnswer ? (
                            <span className={theoreticalFactual.correct ? styles.success_text : styles.failed_text}>{theoreticalFactual.factualAnswer}</span>
                        ) : !theoreticalFactual.factualAnswersNumbers.length && <span>Ответ не указан</span>}
                    </>
                )}
            </ul>
            {!withoutResult && (
                <>
                    <span className={styles.question_title}>результат</span>
                    {theoreticalFactual && (
                        <span className={cx(styles.question_text, theoreticalFactual.correct ? styles.question_text_correct : styles.question_text_wrong)}>
                            {theoreticalFactual.correct ? 'Верно' : 'Не верно'}
                        </span>
                    )}
                    {practicalFactual && (
                        <span className={cx(styles.question_text, practicalFactual.correct ? styles.question_text_correct : styles.question_text_wrong)}>
                            {practicalFactual.correct ? 'Верно' : 'Не верно'}
                        </span>
                    )}
                </>
            )}
        </div>
    );
};

export interface IProps {
    question: ITheoreticalQuestion | IPracticalQuestion;
    num: number;
    theoreticalFactual?: IFactualTheoreticalQuestionAnswer;
    practicalFactual?: IFactualPracticalQuestionAnswer;
    withoutResult?: boolean;
}

export default ExamQuestion;
